import type { RouteTransfer } from '@/types/RouteTransferType';

/**
 * Finds a destination route from a list of routes that matches the given currency code and optionally country code.
 *
 * @param routes - Array of routes to search through
 * @param currencyCodeWallet - Currency code to match (e.g., 'USD', 'EUR')
 * @param countryCode - Optional country code to match (e.g., 'US', 'BR')
 * @returns The matching route if found, undefined otherwise
 */
export function getRouteDestinatary(
  routes: RouteTransfer[],
  currencyCodeWallet: string,
  countryCode?: string
): RouteTransfer | undefined {
  return routes.find((item) => {
    const hasMatchingCurrency = item.currency.code === currencyCodeWallet;
    const hasMatchingCountry = countryCode
      ? item.country.alpha2Code === countryCode
      : true;
    return hasMatchingCurrency && hasMatchingCountry;
  });
}

/**
 * Finds a route by its destination route ID from a list of routes.
 *
 * @param routes - Array of routes to search through
 * @param routeId - The ID of the route to find
 * @returns The matching route if found, undefined otherwise
 */
export function getRouteByDestinationId(
  routes: RouteTransfer[],
  routeId: number
): RouteTransfer | undefined {
  return routes.find((item) => item.id === routeId);
}

/**
 * Devuelve el `currency.code` de la primera ruta con moneda definida.
 * Acepta varias rutas candidatas (p. ej. UI vs provider) y omite nulas/vacías.
 * @param routes - Candidatas en orden de prioridad.
 * @returns Código de moneda o `undefined` si ninguna aporta uno.
 */
export function resolveRouteCurrency(
  ...routes: Array<RouteTransfer | null | undefined>
): string | undefined {
  for (const route of routes) {
    const currencyCode = route?.currency?.code;
    if (currencyCode) return currencyCode;
  }

  return undefined;
}

